import { Box, Button, Input, Table, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Toggle } from '@mangovoice/mango-ui'
import type { ExtensionWebhookConfig } from '@mangovoice/types'

const MAX_PORT = 65535
const IPV4_PATTERN =
  /^(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)(?:\.(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)){3}$/

export function validateIpPort(value: string): string | null {
  if (!value) return null
  const [ip, port, ...rest] = value.split(':')
  if (rest.length > 0 || !IPV4_PATTERN.test(ip))
    return 'Must be valid IP Address. Ex. (0-255).(0-255).(0-255).(0-255)'
  if (port !== undefined) {
    const p = Number(port)
    if (!Number.isInteger(p) || p < 1 || p > MAX_PORT)
      return 'Must be valid PORT Number. Ex. 8888'
  }
  return null
}

export interface XLDentExtensionTableProps {
  extensions: ExtensionWebhookConfig[]
  isActive: boolean
  isPatchPending: boolean
  onToggle: (ext: ExtensionWebhookConfig, checked: boolean) => void
  onSave: (ext: ExtensionWebhookConfig, remoteUser: string) => void
}

export const XLDentExtensionTable = ({
  extensions,
  isActive,
  isPatchPending,
  onToggle,
  onSave,
}: XLDentExtensionTableProps) => {
  const { t } = useTranslation()
  const [remoteUserValues, setRemoteUserValues] = useState<
    Record<number, string>
  >({})
  const [remoteUserErrors, setRemoteUserErrors] = useState<
    Record<number, string>
  >({})

  const hasAnyError = Object.values(remoteUserErrors).some(Boolean)

  const handleChange = (extId: number, value: string) => {
    setRemoteUserValues((prev) => ({ ...prev, [extId]: value }))
    const err = validateIpPort(value)
    setRemoteUserErrors((prev) => ({ ...prev, [extId]: err ?? '' }))
  }

  const handleSaveAll = () => {
    extensions.forEach((ext) => {
      if (!(ext.ext_id in remoteUserValues)) return
      onSave(ext, remoteUserValues[ext.ext_id])
    })
  }

  return (
    <Box>
      <Text fontWeight="semibold" mb={3}>
        {t('webhooks.xlDent.tableDescription')}
      </Text>
      <Table.Root variant="outline" size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>
              {t('webhooks.xlDent.extensionName')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('webhooks.xlDent.extensionNumber')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('webhooks.xlDent.workstationIp')}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t('webhooks.xlDent.enableDisable')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {!isActive ? (
            <Table.Row>
              <Table.Cell colSpan={4} textAlign="center" color="fg.muted">
                {t('webhooks.xlDent.disabled')}
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              {extensions.map((ext) => {
                const inputValue =
                  remoteUserValues[ext.ext_id] ?? ext.remote_user ?? ''
                const error = remoteUserErrors[ext.ext_id]
                return (
                  <Table.Row key={ext.id}>
                    <Table.Cell>{ext.ext_name}</Table.Cell>
                    <Table.Cell>{ext.ext_number}</Table.Cell>
                    <Table.Cell>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Input
                          size="sm"
                          value={inputValue}
                          onChange={(e) =>
                            handleChange(ext.ext_id, e.target.value)
                          }
                          placeholder={t('webhooks.xlDent.ipPlaceholder')}
                          width="200px"
                        />
                        {error && (
                          <Text color="fg.error" fontSize="xs">
                            {error}
                          </Text>
                        )}
                      </Box>
                    </Table.Cell>
                    <Table.Cell>
                      <Toggle
                        checked={ext.active}
                        onCheckedChange={({ checked }) =>
                          onToggle(ext, checked)
                        }
                        disabled={isPatchPending}
                        colorPalette="brand"
                      />
                    </Table.Cell>
                  </Table.Row>
                )
              })}
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center">
                  <Button
                    size="sm"
                    onClick={handleSaveAll}
                    loading={isPatchPending}
                    disabled={hasAnyError}
                    css={{
                      backgroundColor: 'var(--chakra-colors-brand-solid)',
                      color: 'var(--chakra-colors-brand-contrast)',
                      _hover: {
                        backgroundColor:
                          'var(--chakra-colors-brand-emphasized)',
                      },
                    }}
                  >
                    {t('webhooks.xlDent.update')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            </>
          )}
        </Table.Body>
      </Table.Root>
      <Text fontSize="sm" color="fg.muted" mt={3}>
        {t('webhooks.xlDent.devicesNote')}
      </Text>
    </Box>
  )
}
